/*
 * Typed message catalogues (P12, CONVENTIONS §12).
 *
 * A namespace is a French catalogue (the reference, `as const`) and an English one checked against it by the
 * compiler: same keys, same `{placeholders}`, same `<tags>`, same plural forms. A missing or extra key, or a
 * placeholder that differs, fails `tsc`.
 *
 *   // fr.ts
 *   export const fr = { 'title': 'Mes voyages', 'days': { one: '{count} jour', other: '{count} jours' } } as const;
 *   // en.ts
 *   import type { fr } from './fr';
 *   export const en = translation<typeof fr>()({ 'title': 'My trips', 'days': { one: '{count} day', other: '{count} days' } });
 *   // index.ts
 *   export const messages = defineNamespace('trips', fr, en);
 *
 * Messages: plain text with `{name}` placeholders; a plural message is `{ one, other }` (optional `zero` for an
 * exact 0) chosen with `count` by the language's plural rules (French: 0 and 1 are singular; English: only 1);
 * rich messages mark parts with `<tag>…</tag>` (no nesting) for `t.rich`.
 */
import { createElement, Fragment, type ReactNode } from 'react';
import { getLocale, type Locale } from './locale';

export interface PluralMessage {
  /** Exact 0 («Aucun avis»), when the language's `one`/`other` would read badly. */
  readonly zero?: string;
  readonly one: string;
  readonly other: string;
}
export type Message = string | PluralMessage;
export type Catalogue = { readonly [key: string]: Message };

/* ── Type-level reading of a message ── */
type TextsOf<M> = M extends string ? M : M extends PluralMessage ? Exclude<M[keyof M], undefined> : never;
type Holes<S> = S extends `${string}{${infer P}}${infer R}` ? P | Holes<R> : never;
type Tags<S> = S extends `${string}<${infer T}>${infer R}` ? (T extends `/${string}` ? Tags<R> : T | Tags<R>) : never;
/** `{placeholder}` names of a message (`count` for every plural message). */
export type ParamNames<M> = Holes<TextsOf<M>> | (M extends PluralMessage ? 'count' : never);
/** `<tag>` names of a rich message. */
export type TagNames<M> = Tags<TextsOf<M>>;

type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
type SameForm<F, E> = F extends string
  ? E extends string
    ? true
    : false
  : E extends PluralMessage
    ? Same<keyof F, keyof E>
    : false;
type CheckOne<F, E> =
  SameForm<F, E> extends false
    ? { 'must have the same form as the French message (text or the same plural forms)': F }
    : Same<ParamNames<F>, ParamNames<E>> extends false
      ? { 'placeholders must match the French message': ParamNames<F> }
      : Same<TagNames<F>, TagNames<E>> extends false
        ? { 'tags must match the French message': TagNames<F> }
        : unknown;
type Check<Fr, En> = {
  readonly [K in keyof Fr]: K extends keyof En ? CheckOne<Fr[K], En[K]> : { 'missing translation': true };
} & { readonly [K in Exclude<keyof En, keyof Fr>]: { 'this key is not in the French catalogue': true } };

/** What a translation of `Fr` looks like once checked. */
export type Translation<Fr> = {
  readonly [K in keyof Fr]: Fr[K] extends string ? string : { readonly [F in keyof Fr[K]]-?: string };
};

/** English catalogue, checked key by key against the French one: `translation<typeof fr>()({ … })`. */
export const translation =
  <Fr extends Catalogue>() =>
  <const En extends Catalogue>(en: En & NoInfer<Check<Fr, En>>): Translation<Fr> =>
    en as unknown as Translation<Fr>;

/* ── Namespaces ── */
export interface Namespace<C extends Catalogue> {
  readonly name: string;
  readonly catalogues: { readonly fr: C; readonly en: Translation<C> };
}

export const defineNamespace = <C extends Catalogue>(name: string, fr: C, en: Translation<C>): Namespace<C> => ({
  name,
  catalogues: { fr, en },
});

/* ── Formatting a message ── */
type Value = string | number;
export type Params<M> = { readonly [P in ParamNames<M>]: P extends 'count' ? number : Value };
type Args<M> = [ParamNames<M>] extends [never] ? [] : [params: Params<M>];
export type RichParams<M> = {
  readonly [P in ParamNames<M>]: P extends 'count' ? number : ReactNode;
} & { readonly [T in TagNames<M>]: (chunks: ReactNode) => ReactNode };
type RichArgs<M> = [ParamNames<M> | TagNames<M>] extends [never] ? [] : [params: RichParams<M>];

export type Key<C extends Catalogue> = keyof C & string;
/** The keys of a namespace: `KeyOf<typeof messages>` (tables that map a code to a message key). */
export type KeyOf<N> = N extends Namespace<infer C> ? Key<C> : never;
/** The keys of a namespace whose message takes no parameter (safe to pass from a lookup table). */
export type PlainKeyOf<N> =
  N extends Namespace<infer C> ? { [K in Key<C>]: [ParamNames<C[K]>] extends [never] ? K : never }[Key<C>] : never;

export interface Translate<C extends Catalogue> {
  /** The message in the language of this `t`, placeholders filled. */
  <K extends Key<C>>(key: K, ...args: Args<C[K]>): string;
  /** Same, with React nodes: `{x}` takes a node, `<b>…</b>` takes `(chunks) => node`. */
  rich<K extends Key<C>>(key: K, ...args: RichArgs<C[K]>): ReactNode;
  /** Whether a computed key exists (e.g. a server code mapped to a message). */
  has(key: string): key is Key<C>;
  readonly locale: Locale;
}

const pluralRules: Record<Locale, Intl.PluralRules> = {
  fr: new Intl.PluralRules('fr'),
  en: new Intl.PluralRules('en'),
};

function pick(message: Message, locale: Locale, count: unknown): string {
  if (typeof message === 'string') return message;
  const n = typeof count === 'number' ? count : Number(count);
  if (n === 0 && message.zero !== undefined) return message.zero;
  return pluralRules[locale].select(Number.isFinite(n) ? n : 0) === 'one' ? message.one : message.other;
}

function lookup(ns: Namespace<Catalogue>, key: string, locale: Locale): Message {
  const own = (ns.catalogues[locale] as Catalogue)[key];
  if (own !== undefined) return own;
  const fr = ns.catalogues.fr[key];
  if (fr !== undefined) return fr;
  if (import.meta.env?.DEV) console.warn(`[i18n] missing key ${ns.name}:${key}`);
  return key;
}

const HOLE = /\{(\w+)\}/g;
const fill = (text: string, params: Readonly<Record<string, unknown>> | undefined) =>
  params ? text.replace(HOLE, (m, name: string) => (name in params ? String(params[name]) : m)) : text;

/** Rich rendering: `{x}` → the node, `<tag>…</tag>` → `params.tag(chunks)`. */
function richText(text: string, params: Readonly<Record<string, unknown>> = {}): ReactNode {
  const out: ReactNode[] = [];
  const TOKEN = /<(\w+)>([\s\S]*?)<\/\1>|\{(\w+)\}/g;
  let last = 0;
  let i = 0;
  const holes = (s: string): ReactNode[] =>
    s.split(/(\{\w+\})/).map((part) => {
      const m = /^\{(\w+)\}$/.exec(part);
      return m?.[1] && m[1] in params ? (params[m[1]] as ReactNode) : part;
    });
  for (let m = TOKEN.exec(text); m; m = TOKEN.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const [, tag, inner = '', hole] = m;
    if (tag) {
      const render = params[tag];
      const chunks = holes(inner).map((c, j) => createElement(Fragment, { key: j }, c));
      out.push(typeof render === 'function' ? (render as (c: ReactNode) => ReactNode)(chunks) : chunks);
    } else if (hole) {
      out.push(hole in params ? (params[hole] as ReactNode) : m[0]);
    }
    last = TOKEN.lastIndex;
    i += 1;
  }
  if (last < text.length) out.push(text.slice(last));
  return i === 0 ? text : out.map((node, k) => createElement(Fragment, { key: k }, node));
}

/** A `t` function for one namespace and one language (default: the current language). */
export function getT<C extends Catalogue>(ns: Namespace<C>, locale: Locale = getLocale()): Translate<C> {
  const base = ns as unknown as Namespace<Catalogue>;
  const t = (key: string, params?: Record<string, unknown>) =>
    fill(pick(lookup(base, key, locale), locale, params?.count), params);
  return Object.assign(t, {
    rich: (key: string, params?: Record<string, unknown>) =>
      richText(pick(lookup(base, key, locale), locale, params?.count), params),
    has: (key: string) => key in ns.catalogues.fr,
    locale,
  }) as unknown as Translate<C>;
}

/**
 * A `t` for non-React code that reads the current language at each call, so it can live at module level:
 * `const tx = liveT(ns.errors); … throw new ApiError(tx('vehicleGone'))`.
 */
export function liveT<C extends Catalogue>(ns: Namespace<C>) {
  return <K extends Key<C>>(key: K, ...args: Args<C[K]>): string => getT(ns)(key, ...args);
}

/** One message in the current language, for non-React code (services, lib): `translate(errors, 'network')`. */
export function translate<C extends Catalogue, K extends Key<C>>(
  ns: Namespace<C>,
  key: K,
  ...args: Args<C[K]>
): string {
  return getT(ns)(key, ...args);
}

/**
 * A map whose values read the current language at each access, for tables keyed by a server code
 * (`AUTH_MESSAGES[code]`). Keys are enumerable, so `code in map` and `Object.keys(map)` work.
 */
export function messageMap<C extends Catalogue, M extends Record<string, PlainKeyOf<Namespace<C>>>>(
  ns: Namespace<C>,
  keys: M,
): { readonly [P in keyof M]: string } {
  const map = {} as { [P in keyof M]: string };
  for (const [code, key] of Object.entries(keys)) {
    const t = (locale: Locale) => (getT(ns, locale) as unknown as (k: string) => string)(key);
    Object.defineProperty(map, code, { enumerable: true, get: () => t(getLocale()) });
  }
  return map;
}

/**
 * Validation message for zod, read in the current language when the issue is raised:
 * `z.string().min(1, zodMessage(messages, 'email.required'))`.
 */
export const zodMessage = <C extends Catalogue, K extends Key<C>>(ns: Namespace<C>, key: K, ...args: Args<C[K]>) => ({
  error: () => getT(ns)(key, ...args),
});
